/* eslint-disable max-len */
import React, { useMemo, useCallback, useState, ChangeEvent } from 'react'
import Button from '@components/common/Button/Button.component'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import TagBadge, {
  getNoteTagDisplayLabel,
} from '@components/common/TagBadge/TagBadge.component'
import { SearchWithFilter } from '@components/common'
import { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'
import { INote, NoteContextType } from '@dataTypes/note.type'
import { formatDateUS } from '@utils/date.utils'
import {
  formatAppointmentContextDisplay,
  getContextItemDisplayName,
} from '@utils/noteFilter.util'
import {
  formatNoteTitleWithVersion,
  hasMultipleVersionsInChain,
  isLatestNoteInVersionChain,
} from '@utils/noteVersion.util'
import { Form } from 'react-bootstrap'
import NotesDownloadNoteModal from '@components/pages/Notes/Modals/NotesDownloadNoteModal.component'
import { ClipMode, TextAlign } from '@constants/Common/GridComponent.contants'
import { EmptyState } from '@components/ui'

/** Set to `true` to show delete again on each note row's ActionButtons (toolbar or overflow menu). */
const SHOW_DELETE_IN_NOTE_ACTIONS = false

const getNoteSnippet = (body: string): string => {
  const plain = (body || '').replace(/<[^>]*>/g, '')
  return plain.length > 100 ? plain.substring(0, 100) + '...' : plain
}

const getStringFromNoteDetails = (
  details: Record<string, unknown> | null | undefined,
  key: string,
): string | null => {
  if (!details) return null
  const value = details[key]
  return typeof value === 'string' && value.trim() ? value : null
}

const getContextNameFromNoteDetails = (
  details: Record<string, unknown> | null | undefined,
): string | null => {
  const title =
    getStringFromNoteDetails(details, 'title') ??
    getStringFromNoteDetails(details, 'name') ??
    getStringFromNoteDetails(details, 'display_name')
  if (title) return title
  const appointmentDate = getStringFromNoteDetails(details, 'appt_date')
  const appointmentStart = getStringFromNoteDetails(details, 'appt_start')
  if (appointmentDate && appointmentStart) {
    const formatted = formatAppointmentContextDisplay(
      appointmentDate,
      appointmentStart,
    )
    if (formatted) return formatted
    return `${appointmentDate} - ${appointmentStart}`
  }
  return null
}

/** Prefer `appt_date` + `appt_start` over API `context_display_name` (often 24h, no am/pm). */
const getAppointmentContextDisplayForTable = (note: INote): string | null => {
  const ctx = String(note.context_type ?? '').toLowerCase()
  if (ctx !== NoteContextType.APPOINTMENT && ctx !== 'appointment') {
    return null
  }
  const details = note.context_details ?? undefined
  const apptDate =
    getStringFromNoteDetails(details, 'appt_date') ??
    (note.appointment?.appt_date != null &&
    String(note.appointment.appt_date).trim() !== ''
      ? String(note.appointment.appt_date)
      : null)

  const apptStart =
    getStringFromNoteDetails(details, 'appt_start') ??
    (note.appointment?.appt_start != null &&
    String(note.appointment.appt_start).trim() !== ''
      ? String(note.appointment.appt_start)
      : null)
  if (!apptDate || !apptStart) return null
  return formatAppointmentContextDisplay(apptDate, apptStart)
}

const isGeneralPatientContext = (note: INote): boolean => {
  const ctx = String(note.context_type ?? '').toLowerCase()
  return ctx === NoteContextType.PATIENT || ctx === 'patient'
}

const getNoteContextNameForTable = (note: INote): string => {
  const fromAppointment = getAppointmentContextDisplayForTable(note)
  if (fromAppointment) return fromAppointment

  // General notes have no linked item; Tag column already shows "General".
  if (isGeneralPatientContext(note)) return '-'

  const named = note.context_display_name?.trim()
  if (named) return named
  const fromDetails = getContextNameFromNoteDetails(
    note.context_details ?? undefined,
  )
  if (fromDetails) return fromDetails
  const fallback = getContextItemDisplayName(
    note.context_type,
    note.context_id ?? null,
    undefined,
  )
  if (fallback === 'No Context') return '-'
  return fallback
}

type NotesMainContentProps = {
  selectedNotes: INote[]
  patientName: string
  isSidebarOpen?: boolean
  onToggleSidebar?: () => void
  onAddNote: () => void
  /** When true, add note controls are disabled (e.g. claim notes when status is not Rejected). */
  addNoteDisabled?: boolean
  onViewNote?: (note: INote) => void
  onEditNote?: (note: INote) => void
  onDeleteNote?: (note: INote) => void
  onDownloadNote?: (note: INote) => void
  hideTop?: boolean
  renderTopOnly?: boolean
  /** When true, show a blank area instead of no-data content (e.g. while notes are loading). */
  isLoading?: boolean
  /** When provided, search is controlled by parent (shared with top bar). */
  searchQuery?: string
  onSearchChange?: (event: ChangeEvent<HTMLInputElement>) => void
  filters?: FilterSelection
  onFilterChange?: (selectedFilters: FilterSelection) => void
  onResetFilters?: () => void
  /** When set (e.g. from Charting View note), this context type is added to the tag filter options so it appears and can be selected even when there are no notes yet. */
  presetContextType?: string
  /** Side panel notes overlay: single top row — back control before title/search (avoids a second chrome bar). */
  topOverlayBack?: {
    onClick: () => void
    label: string
  }
  /** When no patient is selected, re-open the tray-owned select-patient modal. */
  onSelectPatient?: () => void
}

const NotesMainContent: React.FC<NotesMainContentProps> = ({
  selectedNotes,
  patientName,
  isSidebarOpen = true,
  onToggleSidebar,
  onAddNote,
  addNoteDisabled = false,
  onViewNote,
  onEditNote,
  onDeleteNote,
  onDownloadNote,
  hideTop = false,
  renderTopOnly = false,
  isLoading = false,
  searchQuery: searchQueryProp,
  onSearchChange: onSearchChangeProp,
  filters: filtersProp,
  onFilterChange: onFilterChangeProp,
  onResetFilters: onResetFiltersProp,
  presetContextType,
  topOverlayBack,
  onSelectPatient,
}) => {
  const [internalSearchQuery, setInternalSearchQuery] = useState<string>('')
  const [internalFilters, setInternalFilters] = useState<FilterSelection>({
    date: [],
    tag: [],
  })

  const isControlled =
    searchQueryProp !== undefined && onSearchChangeProp !== undefined
  const searchQuery = isControlled ? searchQueryProp : internalSearchQuery
  const filters = filtersProp !== undefined ? filtersProp : internalFilters
  const handleViewNote = useCallback(
    (note: INote) => {
      if (onViewNote) onViewNote(note)
    },
    [onViewNote],
  )

  const handleEditNote = useCallback(
    (note: INote) => {
      if (onEditNote) onEditNote(note)
    },
    [onEditNote],
  )

  const handleDeleteNote = useCallback(
    (note: INote) => {
      if (onDeleteNote) onDeleteNote(note)
    },
    [onDeleteNote],
  )

  const [noteToDownload, setNoteToDownload] = useState<INote | null>(null)

  const handleDownloadNote = useCallback((note: INote) => {
    setNoteToDownload(note)
  }, [])

  const handleConfirmDownload = useCallback(() => {
    if (noteToDownload && onDownloadNote) {
      onDownloadNote(noteToDownload)
    }
    setNoteToDownload(null)
  }, [noteToDownload, onDownloadNote])

  const handleSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (onSearchChangeProp) onSearchChangeProp(event)
      else setInternalSearchQuery(event.target.value)
    },
    [onSearchChangeProp],
  )

  const handleFilterChange = useCallback(
    (selectedFilters: FilterSelection) => {
      if (onFilterChangeProp) {
        onFilterChangeProp({
          date: [],
          tag: [],
          ...(filtersProp ?? internalFilters),
          ...selectedFilters,
        })
      } else {
        setInternalFilters((prevFilters) => ({
          date: [],
          tag: [],
          ...prevFilters,
          ...selectedFilters,
        }))
      }
    },
    [onFilterChangeProp, filtersProp, internalFilters],
  )

  const handleResetFilters = useCallback(() => {
    if (onResetFiltersProp) onResetFiltersProp()
    else {
      setInternalSearchQuery('')
      setInternalFilters({ date: [], tag: [] })
    }
  }, [onResetFiltersProp])

  const filterSelectionList = useMemo(() => {
    const dateOptions = new Set<string>()
    const tagOptions = new Set<string>()

    selectedNotes.forEach((note) => {
      const dateStr = note.date_time_created_at ?? ''
      if (dateStr) {
        const date = new Date(dateStr)
        const monthYear = date.toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric',
        })
        dateOptions.add(monthYear)
      }

      if (note.context_type) {
        tagOptions.add(note.context_type as string)
      }
    })

    // Include context type from origin (e.g. Charting History) so filter shows "Charting" / "Tx plan" and can be selected even with no notes
    if (presetContextType?.trim()) {
      tagOptions.add(presetContextType.trim())
    }

    return {
      date: Array.from(dateOptions).sort(),
      tag: Array.from(tagOptions).sort(),
    }
  }, [selectedNotes, presetContextType])

  const tagFilterCustomRenderers = useMemo(() => {
    const tagList = filterSelectionList.tag ?? []
    const renderers: Record<
      string,
      (
        option: string,
        isChecked: boolean,
        onChange: () => void,
      ) => React.ReactNode
    > = {}
    tagList.forEach((option) => {
      renderers[`tag:${option}`] = (_, isChecked, onChange) => (
        <Form.Check
          type="checkbox"
          label={getNoteTagDisplayLabel(option)}
          checked={isChecked}
          onChange={onChange}
        />
      )
    })
    return renderers
  }, [filterSelectionList.tag])

  const filteredNotes = useMemo(() => {
    const snippetMap = new Map<string, string>()
    selectedNotes.forEach((n) => {
      snippetMap.set(n.note_id, getNoteSnippet(n.body))
    })

    const filtered = selectedNotes.filter((note) => {
      const snippet = snippetMap.get(note.note_id) ?? ''
      const matchesSearch =
        searchQuery === '' ||
        note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        snippet.toLowerCase().includes(searchQuery.toLowerCase())

      const dateStr = note.date_time_created_at ?? ''
      const matchesDate =
        !filters.date ||
        filters.date.length === 0 ||
        (dateStr &&
          filters.date.some((selectedDate: string) => {
            const noteDate = new Date(dateStr)
            const noteMonthYear = noteDate.toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric',
            })
            return noteMonthYear === selectedDate
          }))

      const matchesTag =
        !filters.tag ||
        filters.tag.length === 0 ||
        (note.context_type && filters.tag.includes(note.context_type as string))

      return matchesSearch && matchesDate && matchesTag
    })

    return filtered.sort((a, b) => {
      const aTime = new Date(
        a.date_time_updated_at ?? a.date_time_created_at ?? 0,
      ).getTime()
      const bTime = new Date(
        b.date_time_updated_at ?? b.date_time_created_at ?? 0,
      ).getTime()
      return bTime - aTime
    })
  }, [selectedNotes, searchQuery, filters])

  const columns = useMemo(
    () => [
      {
        field: 'note_id',
        isPrimaryKey: true,
        visible: false,
        width: 0,
      },
      {
        field: '',
        headerText: '',
        width: 35,
        template: () => (
          <div className="note-icon-column">
            <i className="mgc mgc_document_2_fill" />
          </div>
        ),
      },
      {
        field: 'note_title',
        headerText: 'Title',
        clipMode: ClipMode.EllipsisWithTooltip,
        width: 320,
      },
      {
        field: 'context_display_name_text',
        headerText: 'Context',
        textAlign: TextAlign.Left,
        headerTextAlign: TextAlign.Left,
        clipMode: ClipMode.EllipsisWithTooltip,
        width: 180,
      },
      {
        field: 'context_type',
        headerText: 'Tag',
        textAlign: TextAlign.Left,
        headerTextAlign: TextAlign.Left,
        width: 100,
        template: (props: INote) => (
          <div className="note-tags-column">
            <TagBadge tag={props.context_type as string} />
          </div>
        ),
      },
      {
        field: 'date_time_updated_at_text',
        headerText: 'Last edited',
        textAlign: TextAlign.Left,
        headerTextAlign: TextAlign.Left,
        width: 100,
      },
      {
        field: 'date_time_created_at_text',
        headerText: 'Date created',
        textAlign: TextAlign.Left,
        headerTextAlign: TextAlign.Left,
        width: 100,
      },
      {
        field: '',
        headerText: 'Actions',
        textAlign: TextAlign.Center,
        headerTextAlign: TextAlign.Center,
        width: 120,
        template: (props: INote) => (
          <div className="notes-actions-column">
            <ActionButtons
              onView={() => handleViewNote(props)}
              {...(onEditNote ? { onEdit: () => handleEditNote(props) } : {})}
              {...(SHOW_DELETE_IN_NOTE_ACTIONS && onDeleteNote
                ? { onDelete: () => handleDeleteNote(props) }
                : {})}
              showEdit={Boolean(onEditNote)}
              showDelete={false}
              disableEdit={!isLatestNoteInVersionChain(props, selectedNotes)}
              disabledEditTooltip="Only the latest version of this note can be edited."
              customMiddleButton={
                <Button
                  className="act-button act-button-download"
                  onClick={() => handleDownloadNote(props)}
                  title="Download"
                >
                  <i className="mgc mgc_download_2_fill" />
                </Button>
              }
            />
          </div>
        ),
      },
    ],
    [
      handleViewNote,
      handleEditNote,
      handleDeleteNote,
      handleDownloadNote,
      selectedNotes,
      onDeleteNote,
      onEditNote,
    ],
  )

  const gridData = useMemo(
    () =>
      filteredNotes.map((note) => ({
        ...note,
        note_title: hasMultipleVersionsInChain(note, selectedNotes)
          ? formatNoteTitleWithVersion({
              title: note.title ?? '',
              version: note.version,
            })
          : note.title,
        context_display_name_text: getNoteContextNameForTable(note),
        date_time_updated_at_text: note.date_time_updated_at
          ? formatDateUS(note.date_time_updated_at)
          : '',
        date_time_created_at_text: note.date_time_created_at
          ? formatDateUS(note.date_time_created_at)
          : '',
      })),
    [filteredNotes, selectedNotes],
  )

  const isNoPatientSelected = patientName === 'Select a patient'
  const hasNoNotes = selectedNotes.length === 0
  const disableAddNoteButton =
    isNoPatientSelected || addNoteDisabled || hasNoNotes

  if (renderTopOnly) {
    return (
      <div className="notes-main-content-top">
        <div className="notes-for">
          {!isSidebarOpen && onToggleSidebar && (
            <div className="notes-sidebar-toggle" onClick={onToggleSidebar}>
              <i className="mgc mgc_layout_leftbar_open_line"></i>
            </div>
          )}
          {!isSidebarOpen && onToggleSidebar && (
            <div className="notes-for-inner">
              <div className="notes-for-text">
                {isNoPatientSelected ? 'Notes' : `Notes for ${patientName}`}
              </div>
            </div>
          )}
        </div>
        <div className="notes-for-search">
          <div className="notes-search-filter-wrapper notes-search-filter-custom-width">
            <SearchWithFilter
              searchIcon="mgc mgc_search_2_line"
              filterIcon="mgc mgc_settings_2_line"
              placeholder="Search note"
              searchQuery={searchQuery}
              onChange={handleSearch}
              onReset={handleResetFilters}
              showResetButton={true}
              className="notes-search-filter-main"
              filters={filterSelectionList}
              selectedFilters={filters}
              onFilterChange={handleFilterChange}
              filterLabels={{
                date: 'Date',
                tag: 'Tag',
              }}
              customRenderers={tagFilterCustomRenderers}
            />
          </div>
          <Button
            size="md"
            className="button-primary button-icon-round"
            onClick={onAddNote}
            disabled={disableAddNoteButton}
            title={
              addNoteDisabled
                ? 'Notes can only be added when the claim is rejected.'
                : undefined
            }
          >
            Add note
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="notes-main-content-view">
      {!hideTop && (
        <div
          className={`notes-main-content-top ${!isSidebarOpen ? 'main-content-top-padd' : ''} ${topOverlayBack ? 'notes-main-content-top--with-overlay-back' : ''}`}
        >
          <div className="notes-for">
            {topOverlayBack ? (
              <>
                <button
                  type="button"
                  className="note-editor-overlay-back notes-main-content-overlay-back"
                  onClick={topOverlayBack.onClick}
                >
                  <i className="mgc mgc_left_line" aria-hidden={true} />
                </button>
                <div className="notes-for-inner">
                  <div className="notes-for-text">
                    {isNoPatientSelected ? 'Notes' : `Notes for ${patientName}`}
                  </div>
                </div>
              </>
            ) : (
              <>
                {!isSidebarOpen && onToggleSidebar && (
                  <div
                    className="notes-sidebar-toggle"
                    onClick={onToggleSidebar}
                  >
                    <i className="mgc mgc_layout_leftbar_open_line"></i>
                  </div>
                )}
                {!isSidebarOpen && onToggleSidebar && (
                  <div className="notes-for-inner">
                    <div className="notes-for-text">
                      {isNoPatientSelected
                        ? 'Notes'
                        : `Notes for ${patientName}`}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="notes-for-search">
            <div className="notes-search-filter-wrapper notes-search-filter-custom-width">
              <SearchWithFilter
                searchIcon="mgc mgc_search_2_line"
                filterIcon="mgc mgc_settings_2_line"
                placeholder="Search note"
                searchQuery={searchQuery}
                onChange={handleSearch}
                onReset={handleResetFilters}
                showResetButton={true}
                className="notes-search-filter-main"
                filters={filterSelectionList}
                selectedFilters={filters}
                onFilterChange={handleFilterChange}
                filterLabels={{
                  date: 'Date',
                  tag: 'Tag',
                }}
                customRenderers={tagFilterCustomRenderers}
              />
            </div>
            <Button
              size="md"
              className="button-primary button-icon-round"
              onClick={onAddNote}
              disabled={disableAddNoteButton}
              title={
                addNoteDisabled
                  ? 'Notes can only be added when the claim is rejected.'
                  : undefined
              }
            >
              Add note
            </Button>
          </div>
        </div>
      )}
      <div className="notes-main-content-body">
        {isLoading ? (
          <div className="notes-main-content-body-blank" aria-hidden="true" />
        ) : isNoPatientSelected ? (
          <EmptyState
            className="notes-empty"
            iconClass="mgc mgc_document_fill"
            title="Notes"
            description="Select a patient to view notes"
            actionLabel={onSelectPatient ? 'Select a patient' : undefined}
            onAction={onSelectPatient}
          />
        ) : hasNoNotes ? (
          <EmptyState
            className="notes-empty"
            iconClass="mgc mgc_document_fill"
            title="No notes yet"
            description="Add clinical findings or treatment updates to begin the record."
            actionLabel={addNoteDisabled ? undefined : 'Add note'}
            onAction={addNoteDisabled ? undefined : onAddNote}
          />
        ) : filteredNotes.length === 0 ? (
          <EmptyState
            className="notes-empty"
            iconClass="mgc mgc_document_fill"
            title="No notes found"
            description="No notes match your search."
          />
        ) : (
          <>
            <DataGridComponent
              data={gridData}
              columns={columns}
              allowPaging={true}
              allowSelection={true}
              selectionSettings={{
                type: 'Single',
                mode: 'Row',
                enableToggle: false,
              }}
            />
          </>
        )}
      </div>

      <NotesDownloadNoteModal
        show={!!noteToDownload}
        noteName={noteToDownload?.title ?? ''}
        onCancel={() => setNoteToDownload(null)}
        onDownload={handleConfirmDownload}
      />
    </div>
  )
}

export default NotesMainContent
